// Design tokens for the Connect-AI screens.
//
// These screens are drawn against the brand book directly instead of the MUI palette: the
// approved design is a light, paper-coloured page with fixed ink/orange contrast pairs, and
// resolving them through the theme would make them shift with dark mode — the sales page and
// the wizard would stop matching the design they were signed off against. Everything here is
// the same source of truth the onboarding uses, re-exported so there is one palette and not
// two that drift.
import { onboardingColors } from '../forms/bot-onboarding/onboardingBrandIdentity';

// The light-mode literals: the source of truth. `caiColors` below derives from this map,
// so the `var()` fallback and light mode are provably the same bytes, not just a test that
// says so.
export const caiLightColors = {
  orange: onboardingColors.orange, // #FF7235 — CTAs, current step, key numbers
  orangeDark: '#E85B1F', // orange text on light tints: orange-on-paper fails WCAG
  orangeTint: '#FFF1EA', // orange at 8% on paper, for the "ask your AI" callout
  ink: onboardingColors.inkNavy, // #040433 — dark surfaces and primary text
  ink800: onboardingColors.ink800, // #1A1A4A — body copy on paper
  purple: onboardingColors.signalPurple, // #5D3CBF — the user's bubble in the demo, support only
  onPurple: onboardingColors.pureWhite, // #FFFFFF — label on the purple bubble fill
  onInk: onboardingColors.paper, // #FAF7F2 — text on a deliberately dark panel (inkPanel)
  paper2: onboardingColors.paper2, // #F2EDE3 — tool avatars, quiet fills
  surface: onboardingColors.pureWhite, // #FFFFFF — card fill
  onFill: onboardingColors.pureWhite, // #FFFFFF — label on an orange fill
  onDanger: onboardingColors.pureWhite, // #FFFFFF — label on a danger fill · 4.86:1 on #D9342B
  inkPanel: onboardingColors.inkNavy, // #040433 — deliberately dark panel background
  fog: onboardingColors.fog, // #7777A0 — metadata
  muted: '#5C5C82', // secondary copy: fog is too light for a full paragraph
  faint: '#9A9AB8', // disabled labels, step numbers not reached yet
  success: onboardingColors.state.success, // #1F8F5E — completed step, "works" badge
  onSuccess: onboardingColors.pureWhite, // #FFFFFF — label on the "done" step fill
  warning: onboardingColors.state.warning, // #D99400 — "Enterprise only"
  // Red, not orange: orange is the colour of every CTA that moves the user forward, and the
  // destructive actions here (cut access, cancel the add-on) are the ones that don't. Kept as
  // tokens because the same red is used in four files and drifted as a literal in six places.
  danger: '#D9342B',
  dangerBorder: 'rgba(217, 52, 43, 0.28)',
  dangerTint: 'rgba(217, 52, 43, 0.06)',
  // The quiet fill under a summary's last row — near-white on white, not the beige paper.
  paperFaint: '#FCFBF9',
  // Warm app ground behind the white panels (top bar); the page background in dark.
  ground: '#FAF7F2',
  // Hairlines and fills are the ink at low alpha rather than a grey: a neutral grey over
  // paper reads cold next to the navy text.
  border: 'rgba(4, 4, 51, 0.10)',
  borderStrong: 'rgba(4, 4, 51, 0.18)',
  fill: 'rgba(4, 4, 51, 0.06)',
  shadow: '0 1px 2px rgba(4, 4, 51, 0.06)',
  shadowLifted: '0 2px 8px rgba(4, 4, 51, 0.08)',
  // Text editors: selected text and search hits (the current hit reuses `selection`).
  selection: 'rgba(255, 114, 53, 0.22)',
  searchMatch: 'rgba(217, 148, 0, 0.22)',
  // Version diff: changed lines, and the changed words inside them.
  diffAdded: 'rgba(31, 143, 94, 0.10)',
  diffAddedStrong: 'rgba(31, 143, 94, 0.26)',
  diffRemoved: 'rgba(217, 52, 43, 0.08)',
  diffRemovedStrong: 'rgba(217, 52, 43, 0.22)',
  // Chat bubbles: AI agent (purple) and human agent (green); the customer uses surface.
  chatAiBubble: 'rgba(93, 60, 191, 0.09)',
  chatAiBorder: 'rgba(93, 60, 191, 0.18)',
  chatHumanBubble: 'rgba(31, 143, 94, 0.09)',
  chatHumanBorder: 'rgba(31, 143, 94, 0.22)',
  // Informational callout (purple) and warning callout (amber) tints, borders and text.
  infoTint: 'rgba(93, 60, 191, 0.07)',
  infoBorder: 'rgba(93, 60, 191, 0.18)',
  warningTint: 'rgba(217, 148, 0, 0.10)',
  warningBorder: 'rgba(217, 148, 0, 0.35)',
  warningText: '#8A5D00', // amber text on warningTint
} as const;

// Dark-mode overrides, written per key onto `<html>` by `CaiThemeVars`. A key absent here
// falls through to its `caiLightColors` value in both modes (no dark override needed) —
// `onFill`, `inkPanel`, and `onInk` are invariant across modes for exactly that reason.
//
// Measured against the card fill `#202c33` (the page behind it, `#111b21`, is higher
// contrast still). Ratios and roles are recorded in design.md's Dark Values table.
export const caiDarkColors: Partial<Record<keyof typeof caiLightColors, string>> = {
  // Surface and the text that sits on it land together — the mitigation for the
  // 3d3961da / 2d947652 regression: a converted surface with unconverted text on it.
  surface: '#202c33', // card fill == MUI background.paper
  ink: '#FFFFFF', // primary text · 14.29:1
  ink800: '#C9D3DA', // body copy · 9.40:1
  muted: '#9FB0BB', // secondary copy · 6.40:1
  fog: '#8A9BA8', // metadata · 4.99:1
  faint: '#6B7C8A', // inactive text · 3.32:1, exempt under WCAG 1.4.3
  paper2: '#2A3942', // quiet/hover fill
  paperFaint: '#26333A', // last summary row
  ground: '#111b21', // == MUI background.default
  fill: 'rgba(255, 255, 255, 0.08)', // disabled/chip fill
  border: 'rgba(255, 255, 255, 0.12)', // decorative hairline, == MUI divider
  borderStrong: 'rgba(255, 255, 255, 0.36)', // control boundary · 3.17:1
  shadow: '0 1px 2px rgba(0, 0, 0, 0.40)',
  shadowLifted: '0 2px 8px rgba(0, 0, 0, 0.55)',
  selection: 'rgba(255, 114, 53, 0.34)',
  searchMatch: 'rgba(217, 148, 0, 0.32)',
  diffAdded: 'rgba(76, 195, 138, 0.14)',
  diffAddedStrong: 'rgba(76, 195, 138, 0.32)',
  diffRemoved: 'rgba(242, 139, 130, 0.12)',
  diffRemovedStrong: 'rgba(242, 139, 130, 0.30)',
  // Brand accents. `purple`/`success`/`orangeDark` each hit the trap docs/design-system.md
  // §2 records: a value light enough to read as text is too light for a white label, so the
  // fill and its label move together. `orange`/`warning` already pass on dark and stay put.
  purple: '#9B7AFF', // bubble fill · 4.50:1 — the shipped dark `secondary` in DarkThemeColors
  onPurple: '#040433', // label on it · 6.19:1
  success: '#4CC38A', // status text · 6.45:1
  onSuccess: '#040433', // label on the "done" fill · 8.86:1
  danger: '#F28B82', // error text/border · 5.98:1
  onDanger: '#040433', // label on the danger fill · 8.21:1 — white on #F28B82 is 2.39:1
  dangerBorder: 'rgba(242, 139, 130, 0.40)',
  dangerTint: 'rgba(242, 139, 130, 0.10)',
  orangeDark: '#FF9259', // text and hover fill · 6.47:1 (5.32:1 on the dark tint)
  orangeTint: 'rgba(255, 114, 53, 0.14)', // callout fill
  chatAiBubble: 'rgba(155, 122, 255, 0.16)',
  chatAiBorder: 'rgba(155, 122, 255, 0.30)',
  chatHumanBubble: 'rgba(76, 195, 138, 0.14)',
  chatHumanBorder: 'rgba(76, 195, 138, 0.28)',
  infoTint: 'rgba(155, 122, 255, 0.12)',
  infoBorder: 'rgba(155, 122, 255, 0.28)',
  warningTint: 'rgba(217, 148, 0, 0.14)',
  warningBorder: 'rgba(217, 148, 0, 0.40)',
  warningText: '#F2C14E',
};

// Every token resolved through a CSS custom property, falling back to the light literal.
// Derived instead of hand-written so the fallback can never drift from `caiLightColors`.
export const caiColors = Object.fromEntries(
  (Object.keys(caiLightColors) as Array<keyof typeof caiLightColors>).map((key) => [
    key,
    `var(--cai-${key}, ${caiLightColors[key]})`,
  ]),
) as Record<keyof typeof caiLightColors, string>;

// Orbitron is loaded in _document.tsx. Reserved for the small uppercase overlines
// ("COMPLEMENTO · CONEXIÓN CON IA") — the brand book forbids it in body copy.
export const caiFonts = {
  display: "'Orbitron', 'Figtree', sans-serif",
  ui: "'Figtree', 'DM Sans', sans-serif",
  mono: 'ui-monospace, Menlo, Monaco, "Courier New", monospace',
} as const;

// Page wrapper for org/agent screens drawn in this language. 1200px is the content width
// of a 13" laptop (≈1280–1470px viewport minus the 210px sidebar and page padding), so the
// design is built for it; larger screens get side margins instead of stretched tables.
export const caiPageMaxWidth = 1200;
export const caiPageSx = {
  width: '100%',
  maxWidth: caiPageMaxWidth,
  mx: 'auto',
  color: caiColors.ink,
} as const;

// The overline above a section title. Repeated in four places, so it lives here.
export const caiOverlineSx = {
  fontFamily: caiFonts.display,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.11em',
  textTransform: 'uppercase',
  color: caiColors.orange,
} as const;

// A white card on paper. Lifted is for the one card that carries an action (the price row).
export const caiCardSx = (lifted = false) => ({
  bgcolor: caiColors.surface,
  border: `1px solid ${caiColors.border}`,
  borderRadius: '14px',
  boxShadow: lifted ? caiColors.shadowLifted : caiColors.shadow,
});

// The filled orange CTA. `textTransform: none` on every button here: the design is sentence
// case and MUI uppercases by default.
//
// KNOWN FOLLOW-UP (openspec/changes/connectai-dark-mode): `onFill` (#FFFFFF) on `orange`
// (#FF7235) is 2.72:1, failing WCAG 1.4.3 and `tokens.brand` ("on an orange fill the label
// is navy, 7.20:1") — in light mode, today, before this change. Left untouched here: fixing
// it repaints light mode, which is out of scope for a dark-mode change. Tracked, not fixed.
export const caiPrimaryButtonSx = {
  fontFamily: caiFonts.ui,
  fontSize: '15.5px',
  fontWeight: 700,
  color: caiColors.onFill,
  bgcolor: caiColors.orange,
  borderRadius: '6px',
  px: 3.5,
  py: 1.6,
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': { bgcolor: caiColors.orangeDark, boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: caiColors.fill, color: caiColors.faint },
} as const;

// One height for every control in a list toolbar (search, selects, buttons), so a row of
// mixed MUI components lines up. Applied by the row, not per control.
export const caiControlHeight = 40;
export const caiToolbarRowSx = {
  '& .MuiOutlinedInput-root:not(.MuiInputBase-multiline)': { height: caiControlHeight },
  '& .MuiButton-root': { height: caiControlHeight, py: 0 },
  // Small selects/fields put their label for a 37px box; recentre it for 40px.
  '& .MuiInputLabel-sizeSmall:not(.MuiInputLabel-shrink)': { transform: 'translate(14px, 10px) scale(1)' },
} as const;

// Paper for menus and popovers opened from redesigned screens: a lifted card.
export const caiFloatingPaperSx = {
  bgcolor: caiColors.surface,
  backgroundImage: 'none',
  color: caiColors.ink,
  border: `1px solid ${caiColors.border}`,
  borderRadius: '12px',
  boxShadow: caiColors.shadowLifted,
  mt: 0.5,
} as const;

// Data tables inside a shared card: quiet header, hairline rows, tinted hover.
export const caiTableSx = {
  '& th': {
    fontFamily: caiFonts.ui,
    fontSize: '12.5px',
    fontWeight: 700,
    color: caiColors.fog,
    borderBottom: `1px solid ${caiColors.border}`,
    whiteSpace: 'nowrap',
  },
  '& td': {
    fontFamily: caiFonts.ui,
    fontSize: '14px',
    color: caiColors.ink,
    borderBottom: `1px solid ${caiColors.border}`,
  },
  '& tbody tr:last-of-type td': { borderBottom: 'none' },
} as const;

// The destructive counterpart of the primary CTA (delete, cancel a send).
export const caiDangerButtonSx = {
  ...caiPrimaryButtonSx,
  color: caiColors.onDanger,
  bgcolor: caiColors.danger,
  '&:hover': { bgcolor: caiColors.danger, filter: 'brightness(0.92)', boxShadow: 'none' },
} as const;

// The quiet counterpart (Cancelar / Volver): text only, no border, so it can't compete.
//
// `bgcolor: transparent` is not redundant: the app theme gives every text+primary Button a
// `primary.light` fill (Components.tsx → MuiButton.textPrimary), which turns these into pink
// pills. sx wins over styleOverrides, so this is where it gets undone.
export const caiGhostButtonSx = {
  fontFamily: caiFonts.ui,
  fontSize: '15px',
  fontWeight: 600,
  color: caiColors.muted,
  bgcolor: 'transparent',
  textTransform: 'none',
  px: 0.5,
  py: 1.5,
  '&:hover': { bgcolor: 'transparent', color: caiColors.ink },
} as const;

// The dashed "connect another / show the rest" affordances. Same theme caveat as above.
export const caiDashedButtonSx = {
  fontFamily: caiFonts.ui,
  fontWeight: 600,
  color: caiColors.orangeDark,
  bgcolor: 'transparent',
  border: '1px dashed rgba(255, 114, 53, 0.45)',
  width: '100%',
  textTransform: 'none',
  '&:hover': { bgcolor: caiColors.orangeTint },
} as const;
